import { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  ListGroup,
  Button,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import type { UserOrganisation } from '../types/types';
import { useSupplierAssessments } from '../hooks/useSupplierAssessments';
import { useActionPlans } from '../hooks/useActionPlans';
import { supabase } from '../lib/supabase';

const aiCreationSteps = [
  'Analyzing your completed assessments',
  'Prioritizing the most important compliance gaps',
  'Drafting remediation steps for your team',
  'Preparing your AI compliance action plan',
];

const aiPlanStyles = {
  hero: {
    border: '1px solid #dbe4ee',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
    background: '#ffffff',
  },
  heroHeader: {
    background:
      'linear-gradient(135deg, #ffffff 0%, #fafcff 60%, #f3f6f9 100%)',
    borderBottom: '1px solid #dbe4ee',
  },
} satisfies Record<string, React.CSSProperties>;

const MyComplianceAiPlan = () => {
  const navigate = useNavigate();

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const { complianceOverview } = useSupplierAssessments(
    selectedOrganisation?.organisation?.id,
  );

  const overview = complianceOverview;
  const hasAiCompliancePlan =
    selectedOrganisation?.organisation?.plan?.features?.['aiCompliancePlan'];

  const {
    plans,
    loading: plansLoading,
    error: plansError,
    refetch: refetchPlans,
  } = useActionPlans(selectedOrganisation?.organisation?.id);

  const [creatingPlan, setCreatingPlan] = useState(false);
  const [createPlanError, setCreatePlanError] = useState<string | null>(null);
  const [aiCreationStepIndex, setAiCreationStepIndex] = useState(0);
  const [showCreateNewPlanModal, setShowCreateNewPlanModal] = useState(false);

  const activePlans = plans.filter((plan) => plan.status !== 'archived');
  const aiPlans = activePlans.filter((plan) => !plan.assigned_by);
  const buyerAssignedPlans = activePlans.filter((plan) => !!plan.assigned_by);
  const latestSelfAssignedPlan = [...aiPlans].sort((leftPlan, rightPlan) => {
    const leftTime = leftPlan.created_at
      ? new Date(leftPlan.created_at).getTime()
      : 0;
    const rightTime = rightPlan.created_at
      ? new Date(rightPlan.created_at).getTime()
      : 0;
    return rightTime - leftTime;
  })[0];
  const hasSelfAssignedPlan = aiPlans.length > 0;
  const canCreateActionPlan =
    !!overview && overview.assessment_count > 0 && !hasSelfAssignedPlan;

  const latestSelfAssignedPlanCreatedAt = latestSelfAssignedPlan?.created_at
    ? new Date(latestSelfAssignedPlan.created_at)
    : null;
  const latestSelfAssignedPlanAgeInDays = latestSelfAssignedPlanCreatedAt
    ? Math.floor(
        (Date.now() - latestSelfAssignedPlanCreatedAt.getTime()) /
          (1000 * 60 * 60 * 24),
      )
    : null;
  const canCreateReplacementPlan =
    latestSelfAssignedPlanAgeInDays !== null &&
    latestSelfAssignedPlanAgeInDays >= 30;
  const daysUntilNextPlan =
    latestSelfAssignedPlanAgeInDays !== null
      ? Math.max(30 - latestSelfAssignedPlanAgeInDays, 0)
      : null;

  const getPlanProgress = (
    completedItemsCount?: number,
    totalItemsCount?: number,
  ) => {
    if (!totalItemsCount) return 0;
    return Math.round(((completedItemsCount ?? 0) / totalItemsCount) * 100);
  };

  const getPlanVariant = (
    completedItemsCount?: number,
    totalItemsCount?: number,
  ) => {
    if (!totalItemsCount) return 'secondary';
    if ((completedItemsCount ?? 0) >= totalItemsCount) return 'success';
    return 'info';
  };

  const isPlanComplete = (
    completedItemsCount?: number,
    totalItemsCount?: number,
  ) => !!totalItemsCount && (completedItemsCount ?? 0) >= totalItemsCount;

  const formatDate = (value: string | null) => {
    if (!value) return 'Unknown date';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown date';

    return date.toLocaleDateString();
  };

  useEffect(() => {
    if (!creatingPlan) {
      setAiCreationStepIndex(0);
      return;
    }

    const intervalId = window.setInterval(() => {
      setAiCreationStepIndex((currentIndex) =>
        currentIndex === aiCreationSteps.length - 1 ? 0 : currentIndex + 1,
      );
    }, 1800);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [creatingPlan]);

  const handleCreateActionPlan = async () => {
    const orgId = selectedOrganisation?.organisation?.id;
    if (!orgId) return;

    if ((overview?.average_compliance_percentage ?? 0) > 85) {
      setCreatePlanError(
        "Your compliance is already high, so we don't have enough negative measuring points to create an action plan.",
      );
      return;
    }

    setCreatingPlan(true);
    setCreatePlanError(null);
    setShowCreateNewPlanModal(false);

    const { data, error: assessmentsError } = await supabase.rpc(
      'get_supplier_assessments_by_org_id',
      { supplier_org: orgId },
    );

    if (assessmentsError) {
      setCreatePlanError(assessmentsError.message);
      setCreatingPlan(false);
      return;
    }

    const assessmentIds = (data ?? [])
      .map(
        (assessment: { assessment_id?: string | null }) =>
          assessment.assessment_id,
      )
      .filter(
        (assessmentId: string | null | undefined): assessmentId is string =>
          !!assessmentId,
      );

    if (assessmentIds.length === 0) {
      setCreatePlanError(
        'Please complete at least one assessment to create a compliance action plan.',
      );
      setCreatingPlan(false);
      return;
    }

    const { data: createData, error: createError } =
      await supabase.functions.invoke('create-ai-action-plan', {
        body: {
          org_id: orgId,
          assessment_ids: assessmentIds,
        },
      });

    if (createError) {
      setCreatePlanError(createError.message);
      setCreatingPlan(false);
      return;
    }

    const createdPlanId =
      (createData as { plan_id?: string | null })?.plan_id ?? null;

    if (createdPlanId) {
      navigate(`/action-plan/${createdPlanId}`);
      setCreatingPlan(false);
      return;
    }

    await refetchPlans();
    setCreatingPlan(false);
  };

  return (
    <Container fluid>
      <Card style={aiPlanStyles.hero} className="mb-4">
        <div style={aiPlanStyles.heroHeader} className="p-4 p-lg-5">
          <Row className="g-4 align-items-center">
            <Col lg={8}>
              <div
                className="text-uppercase fw-semibold mb-2"
                style={{
                  letterSpacing: '0.12em',
                  fontSize: '0.78rem',
                  color: '#64748b',
                }}
              >
                Compliance Module
              </div>
              <h3 className="mb-2">AI Compliance Action Plan</h3>
              <p className="text-muted mb-0">
                Build your own AI-generated compliance action plan based on your
                assessment outcomes and track remediation progress in one place.
              </p>
            </Col>
            <Col lg={4}>
              <div className="d-grid gap-2">
                <Link to="/my-compliance" className="btn btn-outline-primary">
                  Back to My Compliance
                </Link>
              </div>
            </Col>
          </Row>
        </div>
      </Card>

      {hasAiCompliancePlan ? (
        <>
          <Card className="mb-4 border-info">
            <Card.Body>
              <h5 className="mb-3">
                AI Compliance Action Plan <i className="bi bi-stars"></i>
              </h5>
              <p>
                Build your own AI-generated compliance action plan based on your
                assessment outcomes and track remediation progress in one place.
              </p>
              {plansLoading ? (
                <p className="mb-3">Loading AI compliance action plan...</p>
              ) : plansError ? (
                <p className="text-danger mb-3">
                  Error loading AI compliance action plan: {plansError}
                </p>
              ) : aiPlans.length === 0 ? (
                <p className="mb-3">
                  No AI compliance action plan created yet.
                </p>
              ) : !creatingPlan ? (
                <ListGroup className="mb-3">
                  {aiPlans.map((plan) => (
                    <ListGroup.Item key={plan.id}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="fw-semibold">Action Plan</div>
                        {isPlanComplete(
                          plan.completed_items_count,
                          plan.total_items_count,
                        ) && (
                          <span className="badge bg-success-subtle text-success border border-success-subtle d-inline-flex align-items-center gap-1">
                            <i className="bi bi-check-circle-fill"></i>
                            Completed
                          </span>
                        )}
                      </div>
                      <div className="text-muted small">
                        {(plan.status ?? 'active').toUpperCase()}
                        {' · '}
                        Created {formatDate(plan.created_at)}
                      </div>
                      <div className="small text-uppercase text-muted fw-semibold mt-2 mb-1">
                        AI plan progress
                      </div>
                      <ProgressBar
                        className="my-2"
                        now={getPlanProgress(
                          plan.completed_items_count,
                          plan.total_items_count,
                        )}
                        label={`${getPlanProgress(
                          plan.completed_items_count,
                          plan.total_items_count,
                        )}%`}
                        variant={getPlanVariant(
                          plan.completed_items_count,
                          plan.total_items_count,
                        )}
                      />
                      <div className="text-muted small mb-2">
                        {plan.completed_items_count ?? 0}/
                        {plan.total_items_count ?? 0} steps completed
                      </div>
                      <Link to={`/action-plan/${plan.id}`}>
                        View action plan
                      </Link>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : null}

              {canCreateActionPlan ? (
                <>
                  <Button
                    variant="primary"
                    onClick={handleCreateActionPlan}
                    disabled={creatingPlan}
                  >
                    <i className="bi bi-lightning-fill"></i>{' '}
                    {creatingPlan
                      ? 'Creating compliance action plan...'
                      : 'Create compliance action plan'}
                  </Button>
                  {creatingPlan && (
                    <div className="mt-3 p-3 rounded-4 border bg-light">
                      <div className="d-flex align-items-center gap-3 mb-2">
                        <Spinner animation="border" size="sm" />
                        <div className="fw-semibold">
                          AI is building your compliance action plan
                        </div>
                      </div>
                      <div className="small fw-semibold text-dark mb-1">
                        {aiCreationSteps[aiCreationStepIndex]}
                      </div>
                      <div className="text-muted small">
                        Reviewing completed assessments, identifying the highest
                        priority gaps, and generating tailored remediation steps
                        for your team.
                      </div>
                    </div>
                  )}
                  {createPlanError && (
                    <p className="text-danger mt-2 mb-0">{createPlanError}</p>
                  )}
                </>
              ) : hasSelfAssignedPlan ? (
                <>
                  <p className="mb-2">
                    A self-assigned compliance action plan already exists.
                  </p>
                  {creatingPlan ? (
                    <div className="mt-3 p-3 rounded-4 border bg-light">
                      <div className="d-flex align-items-center gap-3 mb-2">
                        <Spinner animation="border" size="sm" />
                        <div className="fw-semibold">
                          AI is building your compliance action plan
                        </div>
                      </div>
                      <div className="small fw-semibold text-dark mb-1">
                        {aiCreationSteps[aiCreationStepIndex]}
                      </div>
                      <div className="text-muted small">
                        Reviewing completed assessments, identifying the highest
                        priority gaps, and generating tailored remediation steps
                        for your team.
                      </div>
                    </div>
                  ) : canCreateReplacementPlan ? (
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowCreateNewPlanModal(true)}
                    >
                      Create a new plan
                    </Button>
                  ) : (
                    <div className="text-muted small">
                      You can create a new one in {daysUntilNextPlan} days.
                    </div>
                  )}
                  {createPlanError && (
                    <p className="text-danger mt-2 mb-0">{createPlanError}</p>
                  )}
                </>
              ) : (
                <p>
                  Please complete at least one assessment to create a compliance
                  action plan.
                </p>
              )}
            </Card.Body>
          </Card>

          {buyerAssignedPlans.length > 0 && (
            <Card className="mb-4 border-primary">
              <Card.Body>
                <h5 className="mb-3 text-primary">
                  Assigned Compliance Action Plans
                </h5>
                <p>
                  These action plans were assigned to you by a buyer. You can
                  have multiple assigned plans active at the same time.
                </p>

                <ListGroup>
                  {buyerAssignedPlans.map((plan) => (
                    <ListGroup.Item key={plan.id}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="fw-semibold">
                          Plan {plan.id.slice(0, 8)}
                        </div>
                        {isPlanComplete(
                          plan.completed_items_count,
                          plan.total_items_count,
                        ) && (
                          <span className="badge bg-success-subtle text-success border border-success-subtle d-inline-flex align-items-center gap-1">
                            <i className="bi bi-check-circle-fill"></i>
                            Completed
                          </span>
                        )}
                      </div>
                      <div className="text-muted small">
                        Assigned by buyer {' · '}
                        {(plan.status ?? 'active').toUpperCase()}
                        {' · '}
                        Created {formatDate(plan.created_at)}
                      </div>
                      <div className="small text-uppercase text-muted fw-semibold mt-2 mb-1">
                        Assigned plan progress
                      </div>
                      <ProgressBar
                        className="my-2"
                        now={getPlanProgress(
                          plan.completed_items_count,
                          plan.total_items_count,
                        )}
                        label={`${getPlanProgress(
                          plan.completed_items_count,
                          plan.total_items_count,
                        )}%`}
                        variant={getPlanVariant(
                          plan.completed_items_count,
                          plan.total_items_count,
                        )}
                      />
                      <div className="text-muted small mb-2">
                        {plan.completed_items_count ?? 0}/
                        {plan.total_items_count ?? 0} steps completed
                      </div>
                      <Link to={`/action-plan/${plan.id}`}>
                        View action plan
                      </Link>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          )}
        </>
      ) : (
        <Card className="mb-4 border-primary">
          <Card.Body>
            <h5 className="mb-3 text-primary">Upgrade to AI Compliance Plan</h5>
            <p>
              Upgrade to our AI Compliance Plan to unlock advanced insights and
              personalized recommendations powered by AI. Contact your
              administrator to learn more about the benefits and how to upgrade.
            </p>
            <Link to="/settings?tab=organisation">
              <Button variant="outline-primary">Upgrade Now</Button>
            </Link>
          </Card.Body>
        </Card>
      )}

      <Modal
        show={showCreateNewPlanModal}
        onHide={() => setShowCreateNewPlanModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create a new AI compliance plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Creating a new AI compliance plan will generate a fresh set of
          remediation steps based on your latest completed assessments. Do you
          want to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowCreateNewPlanModal(false)}
            disabled={creatingPlan}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateActionPlan}
            disabled={creatingPlan}
          >
            {creatingPlan ? 'Creating compliance action plan...' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MyComplianceAiPlan;
